/**
 * Container for settings gear (not including left nav)
 *
 * @prettier
 * @author Alexander Nagelberg
 * @since 24.3
 */
//@ts-check
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { ISettingsState } from '../reducers/settingsReducer'
import { clearPage, fetchSettings } from '../actions/settingsActions'
import { ISettingsPage } from '../types/ISettingsPage'
import SettingsTable, { IFormValue } from '../components/SettingsTable'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import LgyLoadingIcon from 'app/components/util/lgy-loading-icon/LgyLoadingIcon'

const SettingsContainer = ({
  clearPage,
  currentPage,
  error,
  fetching,
  fetchSettings,
  posted,
  posting,
  success
}: {
  clearPage: () => void
  currentPage?: ISettingsPage
  error: boolean
  fetching: boolean
  fetchSettings: (id: string) => Promise<void>
  posted: boolean
  posting: boolean
  success: boolean
}) => {
  interface _IParams {
    pageId: string
    sectionId?: string
  }
  const { pageId, sectionId } = useParams() as _IParams
  const section = currentPage?.sections.find(({ id }: { id: string }) => id === sectionId)

  // Provide a key/value map for every setting option (id is key)
  const initialValues = section?.settings?.reduce(
    (acc, { options }) => ({
      ...acc,
      ...options.reduce((acc, { id, value }) => {
        acc[id] = value
        return acc
      }, {} as IFormValue)
    }),
    {} as IFormValue
  )

  useEffect(() => {
    clearPage()
    fetchSettings(pageId)
  }, [clearPage, fetchSettings, pageId, sectionId])

  return (
    <div>
      {posted && success && (
        <LgyAlertDialog
          type='success'
          heading='Update Successful'
          text={undefined}
          paragraph={undefined}
          id={undefined}
        >
          Successfully updated settings
        </LgyAlertDialog>
      )}
      {posted && error && (
        <LgyAlertDialog type='error' heading='Failure Updating' text={undefined} paragraph={undefined} id={undefined}>
          Failed to update settings
        </LgyAlertDialog>
      )}
      {section && (
        <>
          <h1>{section.title}</h1>
          {fetching || posting ? (
            <LgyLoadingIcon className={undefined} />
          ) : (
            <SettingsTable headers={section?.headers} settings={section?.settings} initialValues={initialValues} />
          )}
        </>
      )}
    </div>
  )
}

const mapStateToProps = ({
  adminState: {
    settings: { currentPage, error, fetching, posted, posting, success }
  }
}: {
  adminState: { settings: ISettingsState }
}) => ({ currentPage, error, fetching, posted, posting, success })

export default connect(mapStateToProps, {
  clearPage,
  fetchSettings
})(SettingsContainer)
